import { BusinessObjectBase } from './base';
import type { OrgStructureLevel } from './org-structure-level';
import type { Position } from './position';

export interface OrganizationData {
  readonly id: string;
  readonly name?: string;
  readonly orgStructureLevelId: number;
  readonly orgStructureLevel?: OrgStructureLevel;
  readonly parentId: number;
  readonly parent?: Organization;
  readonly children?: Organization[];
  readonly positions?: Position[];
}

export interface Organization extends Readonly<OrganizationData> {}
export class Organization extends BusinessObjectBase {
  static readonly entityName = 'Organization' as const;
  static readonly plural = 'organizations';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Organization[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Organization(data));
  }

  static async findOne(filter?: any): Promise<Organization | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Organization(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<Organization> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Organization(data);
  }

  static async create(
    data: Omit<OrganizationData, 'id'>,
  ): Promise<Organization> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Organization(result);
  }

  static async updateById(
    id: string,
    data: Partial<OrganizationData>,
  ): Promise<Organization> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new Organization(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
